import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMultiReports } from '../MultiReportsContext';
import { formatDate, getStatusBadge, ADDITIONAL_COLUMNS, COLUMN_GROUPS } from '../constants';
import { Pagination, usePagination } from '../../Pagination';
import { ChevronDown, ChevronUp, Settings, X } from 'lucide-react';

export function DataTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    filteredBookings,
    selectedBookings,
    handleSelectBooking,
    handleSelectAll,
    activeFieldTab,
    activeTab,
    activeFields,
    editingCell,
    editValue,
    setEditValue,
    startInlineEdit,
    saveInlineEdit,
    cancelInlineEdit
  } = useMultiReports();

  // Column selector state - load from localStorage or use defaults
  const [selectedColumns, setSelectedColumns] = React.useState(() => {
    const saved = localStorage.getItem('multiReports_selectedColumns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return ADDITIONAL_COLUMNS.filter(c => c.default).map(c => c.id);
      }
    }
    return ADDITIONAL_COLUMNS.filter(c => c.default).map(c => c.id);
  });
  
  const [showColumnSelector, setShowColumnSelector] = React.useState(false);

  // Save column selection to localStorage
  React.useEffect(() => {
    localStorage.setItem('multiReports_selectedColumns', JSON.stringify(selectedColumns));
  }, [selectedColumns]);

  // Pagination - 10 rows per page
  const { currentPage, setCurrentPage, paginateData } = usePagination(filteredBookings.length, 10);
  const paginatedBookings = paginateData(filteredBookings);

  // Get column value for a booking
  const getColumnValue = (booking, columnId) => {
    // Handle computed columns
    if (columnId === 'brand_model_color') {
      return `${booking.brand_name || ''} ${booking.model || ''} ${booking.color || ''}`.trim() || '-';
    }
    if (columnId === 'exchange_vehicle_details') {
      return `${booking.exchange_brand || ''} ${booking.exchange_model || ''} ${booking.exchange_color || ''}`.trim() || '-';
    }
    if (columnId === 'delivery_date_display') {
      return formatDate(booking.delivery_date);
    }
    
    // Date fields
    if (['date_of_booking', 'date_of_registration', 'delivery_date', 'dms_invoice_date'].includes(columnId)) {
      return formatDate(booking[columnId]);
    }
    
    // Currency fields
    if (columnId === 'customer_balance') {
      const val = booking[columnId];
      return val ? `₹${Number(val).toLocaleString('en-IN')}` : '-';
    }
    if (columnId === 'vendors_purchase_price') {
      const val = booking[columnId];
      if (val === null || val === undefined || val === '') return '-';
      const n = Number(val);
      if (Number.isNaN(n)) return '-';
      return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    return booking[columnId] || '-';
  };

  // Render inline edit cell for active tab fields
  const renderEditableCell = (booking, field) => {
    const isEditing = editingCell?.bookingId === booking.id && editingCell?.field === field.id;
    const currentValue = booking[field.id] || '';

    if (isEditing) {
      if (field.type === 'select') {
        return (
          <div className="flex gap-1 items-center">
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="p-1 border rounded text-xs w-full"
              autoFocus
            >
              <option value="">-- Select --</option>
              {field.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button onClick={saveInlineEdit} className="text-green-600 text-xs px-1">✓</button>
            <button onClick={cancelInlineEdit} className="text-red-600 text-xs px-1">✗</button>
          </div>
        );
      } else if (field.type === 'date') {
        return (
          <div className="flex gap-1 items-center">
            <input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="p-1 border rounded text-xs"
              autoFocus
            />
            <button onClick={saveInlineEdit} className="text-green-600 text-xs px-1">✓</button>
            <button onClick={cancelInlineEdit} className="text-red-600 text-xs px-1">✗</button>
          </div>
        );
      } else {
        return (
          <div className="flex gap-1 items-center">
            <input
              type={field.type === 'number' ? 'number' : 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="p-1 border rounded text-xs w-full"
              autoFocus
            />
            <button onClick={saveInlineEdit} className="text-green-600 text-xs px-1">✓</button>
            <button onClick={cancelInlineEdit} className="text-red-600 text-xs px-1">✗</button>
          </div>
        );
      }
    }

    // Display value
    let displayValue = currentValue || '-';
    if (field.type === 'date' && currentValue) {
      displayValue = formatDate(currentValue);
    } else if (field.type === 'number' && currentValue) {
      displayValue = `₹${Number(currentValue).toLocaleString('en-IN')}`;
    }

    const hasStatus = field.type === 'select' && currentValue;

    return (
      <span 
        className={`cursor-pointer hover:bg-blue-50 px-2 py-1 rounded inline-block text-xs ${
          hasStatus ? getStatusBadge(currentValue) : ''
        }`}
        onClick={() => startInlineEdit(booking.id, field.id, currentValue)}
        title="Click to edit"
      >
        {displayValue}
      </span>
    );
  };

  // Handle navigation to edit with return URL
  const handleFullEdit = (bookingId) => {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    navigate(`/sales/bookings/edit/${bookingId}?returnTo=${returnUrl}`);
  };

  // Toggle column selection
  const toggleColumn = (columnId) => {
    setSelectedColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Select all / Deselect all columns
  const selectAllColumns = () => setSelectedColumns(ADDITIONAL_COLUMNS.map(c => c.id));
  const deselectAllColumns = () => setSelectedColumns([]);
  const resetToDefaults = () => setSelectedColumns(ADDITIONAL_COLUMNS.filter(c => c.default).map(c => c.id));

  // Get selected column definitions in order
  const visibleColumns = ADDITIONAL_COLUMNS.filter(col => selectedColumns.includes(col.id));

  return (
    <div className="space-y-4">
      {/* Header with Pagination and Column Selector */}
      <div className="bg-white rounded-xl border shadow-sm p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              Tab: <strong>{activeTab?.label}</strong>
              {activeFieldTab === 'discounts' && <span className="text-blue-600 ml-2">(Filtered by DMS Invoice Date)</span>}
            </span>
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              <Settings className="w-4 h-4" />
              Customize Columns
              {showColumnSelector ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredBookings.length}
            itemsPerPage={10}
            onPageChange={setCurrentPage}
            showInfo={true}
          />
        </div>

        {/* Column Selector Panel */}
        {showColumnSelector && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm">Select Columns to Display</h4>
              <div className="flex gap-2">
                <button onClick={selectAllColumns} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                  Select All
                </button>
                <button onClick={deselectAllColumns} className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
                  Clear All
                </button>
                <button onClick={resetToDefaults} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200">
                  Reset Defaults
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {ADDITIONAL_COLUMNS.map(col => (
                <label 
                  key={col.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedColumns.includes(col.id) ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(col.id)}
                    onChange={() => toggleColumn(col.id)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-xs">{col.label}</span>
                </label>
              ))}
            </div>
            
            <div className="mt-3 text-xs text-gray-500">
              {selectedColumns.length} of {ADDITIONAL_COLUMNS.length} columns selected
            </div>
          </div>
        )}
      </div>

      {/* 2-Row Card Layout */}
      <div className="space-y-3">
        {paginatedBookings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-indigo-600">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>No bookings match the criteria</h3>
            <p className="text-sm text-slate-500 mt-1.5 max-w-md">Adjust your date range, search, or tab filters to surface bookings. Inline-edit fields appear here as you type.</p>
          </div>
        ) : (
          paginatedBookings.map((booking, index) => {
            const isSelected = selectedBookings.includes(booking.id);
            const rowNumber = (currentPage - 1) * 10 + index + 1;
            
            return (
              <div 
                key={booking.id}
                className={`bg-white/85 backdrop-blur-xl rounded-2xl border border-indigo-100/60 shadow-sm overflow-hidden transition-all duration-180 ease-out hover:shadow-[0_10px_30px_-8px_rgba(79,70,229,0.20)] hover:-translate-y-0.5 ${
                  isSelected ? 'ring-2 ring-indigo-500' : ''
                }`}
              >
                {/* Row 1: Fixed Header Info — premium indigo gradient */}
                <div className="p-3 border-b border-indigo-100/60 flex items-center justify-between text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                  <div className="flex items-center gap-4">
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={() => handleSelectBooking(booking.id)}
                      className="w-4 h-4 accent-indigo-500"
                    />
                    <span className="text-xs text-indigo-100 font-medium">#{rowNumber}</span>
                    <span className="font-bold text-white text-lg" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{booking.booking_no}</span>
                    <span className="text-sm font-medium text-white">
                      {booking.customer_first_name} {booking.customer_last_name || ''}
                    </span>
                    <span className="text-xs text-indigo-100 bg-white/15 backdrop-blur-sm px-2 py-1 rounded-md">
                      {booking.chassis_no || 'No Chassis'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleFullEdit(booking.id)}
                    className="px-4 py-1.5 bg-white/15 backdrop-blur-sm text-white rounded-xl hover:bg-white/25 text-sm font-medium transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                  >
                    Full Edit
                  </button>
                </div>

                {/* Row 2: Two-Column Layout - Selected Columns & Editable Fields */}
                <div className="p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Left Column: Selected Display Columns */}
                    <div className="space-y-2">
                      <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Information</h5>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {visibleColumns.map(col => (
                          <div key={col.id} className="flex flex-col">
                            <span className="text-[10px] text-gray-400 uppercase">{col.label.replace(/^\d+\.\s*/, '')}</span>
                            <span className="text-sm font-medium text-gray-800 truncate" title={getColumnValue(booking, col.id)}>
                              {getColumnValue(booking, col.id)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column: Editable Tab Fields */}
                    <div className="space-y-2">
                      <h5 className="text-xs font-semibold text-indigo-700 uppercase mb-2 tracking-wide">
                        {activeTab?.label} Fields (Click to Edit)
                      </h5>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {activeFields.map(field => (
                          <div key={field.id} className="flex flex-col">
                            <span className="text-[10px] text-gray-400 uppercase">{field.label}</span>
                            <div className="bg-gradient-to-r from-indigo-50/60 to-violet-50/40 rounded-lg p-1 border border-indigo-100/40">
                              {renderEditableCell(booking, field)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bottom Pagination */}
      {filteredBookings.length > 10 && (
        <div className="bg-white rounded-xl border shadow-sm p-3 flex justify-end">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredBookings.length}
            itemsPerPage={10}
            onPageChange={setCurrentPage}
            showInfo={true}
          />
        </div>
      )}
    </div>
  );
}
